import React from 'react';
import { CircleDot, FileText, PencilRuler, Plus, QrCode } from 'lucide-react';

import { getProductTypeName, UNCLASSIFIED } from '@/lib/constants';
import { SelectField } from '@/components/phone/FieldControls';
import {
  CardAction,
  EmptyState,
  PageHeader,
  Pager,
  PhonePage,
  PillButton,
  ScreenLoader,
  SearchBar,
  StatusPill,
  TabRow,
} from '@/components/phone/primitives';
import { Colors, priorityTone } from '@/components/phone/theme';
import { formatDate, isOverdue } from './constants';

/**
 * Every job card, on the admin's phone.
 *
 * The desktop grid puts nine columns across a row; a card here says what is
 * being made, for whom, whether anyone has written on the card yet and where
 * the job stands on the floor. The three lists are tabs and the two narrowing
 * filters open as sheets, the same controls the supervisor's screens use.
 */

function JobCardRow({ jc, onOpen, onFill, onQr }) {
  const unclassified = jc.product_type_code === UNCLASSIFIED;
  const untouched = (jc.completion_percentage || 0) === 0;
  const overdue = isOverdue(jc.expected_delivery_date);
  const who = jc.is_standalone
    ? 'Standalone'
    : [jc.firm_name || jc.customer_name || 'Unknown', jc.firm_name && jc.customer_name].filter(Boolean).join(' — ');

  return (
    <li
      data-testid={`job-card-${jc.id}`}
      className="mt-3 rounded-[10px] border"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`open-job-card-${jc.id}`}
        className="block w-full px-4 pt-3 text-left"
      >
        <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
          {jc.item_name || jc.product_subtype || jc.standalone_name || '—'}
        </span>
        <span
          className="mt-0.5 block truncate text-[12px] font-medium"
          style={{ color: unclassified ? Colors.semantic.caution : Colors.text.secondary }}
        >
          {getProductTypeName(jc.product_type_code, true) || 'Type not set'}
          {who ? ` · ${who}` : ''}
        </span>
        <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
          {jc.is_standalone ? (jc.job_card_id || '') : jc.order_display_id}
          {jc.line_item_quantity ? ` · ${jc.line_item_quantity.toLocaleString()} pcs` : ''}
        </span>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {untouched && <StatusPill label="Not started" tone="critical" testID={`not-started-${jc.id}`} />}
          {jc.current_stage ? (
            <StatusPill label={jc.current_stage.stage_name} tone="accent" dot />
          ) : (
            <StatusPill label={jc.line_item_status === 'completed' ? 'Completed' : 'Not on the floor'}
              tone={jc.line_item_status === 'completed' ? 'positive' : 'neutral'} />
          )}
          <span className="flex items-center gap-1 text-[11px]" style={{ color: Colors.text.tertiary }}>
            <CircleDot className="h-3 w-3" /> {jc.completed_stages || 0}/{jc.total_stages || 0} stages
          </span>
          {jc.order_priority === 'high' && <StatusPill label="Urgent" tone={priorityTone('high')} />}
        </div>

        <p className="py-3 text-[12px]" style={{ color: overdue ? Colors.status.error : Colors.text.secondary }}>
          {jc.expected_delivery_date ? `Due ${formatDate(jc.expected_delivery_date)}${overdue ? ' · overdue' : ''}` : 'No delivery date'}
        </p>
      </button>

      <div className="flex items-center gap-2 border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
        <CardAction icon={PencilRuler} label="Fill" tone="accent" onClick={onFill} testID={`fill-job-card-${jc.id}`} />
        <CardAction icon={QrCode} label="QR sticker" onClick={onQr} testID={`qr-job-card-${jc.id}`} />
      </div>
    </li>
  );
}

export default function PhoneJobCardsList({
  jobCards, loading, stats, pagination, onPage, onSearch,
  tab, onTab, productTypeFilter, onProductType, productConfigs,
  completionFilter, onCompletion, completionOptions,
  canCreate, onCreate, onOpen, onFill, onQr,
}) {
  const tabs = [
    { key: 'active', label: `Active ${stats.active || 0}`, testID: 'stat-active' },
    { key: 'unfilled', label: `Not filled ${stats.unfilled || 0}`, testID: 'stat-unfilled' },
    { key: 'completed', label: `Done ${stats.completed || 0}`, testID: 'stat-completed' },
  ];
  const products = [
    { value: 'all', label: 'All products' },
    ...productConfigs.map((c) => ({
      value: c.product_type_code || c.code,
      label: c.product_type_name || c.name || c.code,
    })),
  ];

  return (
    <PhonePage testID="job-cards-page">
      <PageHeader
        title="Job Cards"
        subtitle={`${pagination.total || stats.total || 0} card${(pagination.total || 0) === 1 ? '' : 's'}`}
        action={canCreate && (
          <PillButton onClick={onCreate} testID="new-job-card-btn">
            <Plus className="h-4 w-4" /> New
          </PillButton>
        )}
      />

      <div className="px-5">
        <SearchBar placeholder="Customer, item, order…" onChange={onSearch} testID="job-cards-search-input" />
        <div className="mt-3">
          <TabRow tabs={tabs} active={tab} onSelect={onTab} />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <SelectField name="product_type" label="Product" value={productTypeFilter} options={products} onChange={onProductType} />
          <SelectField
            name="completion"
            label="Card completion"
            value={completionFilter}
            options={completionOptions.map(([value, label]) => ({ value, label }))}
            onChange={onCompletion}
          />
        </div>
      </div>

      <div className="flex-1 px-5 pb-8">
        {loading ? (
          <ScreenLoader />
        ) : jobCards.length === 0 ? (
          <EmptyState icon={FileText} message="No job cards match these filters" />
        ) : (
          <ul data-testid="job-cards-list">
            {jobCards.map((jc) => (
              <JobCardRow
                key={jc.id}
                jc={jc}
                onOpen={() => onOpen(jc)}
                onFill={() => onFill(jc)}
                onQr={() => onQr(jc)}
              />
            ))}
          </ul>
        )}
        <Pager page={pagination.page} totalPages={pagination.totalPages} onPage={onPage} testID="job-cards-pagination" />
      </div>
    </PhonePage>
  );
}
